import React, { useCallback, useEffect, useState } from 'react'
import { Handle, NodeProps, NodeToolbar, Position, useReactFlow } from '@xyflow/react'
import { ApiVersion } from '../../../../enums/api-version.enum'
import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationService } from '../../../../services'
import { FeedService } from '../../../../services/feed.service'
import { Feed } from '@pushly/models/lib/structs/feeds/feed'
import { getEdgeId } from '../../helpers'
import { JourneyNodeIcon } from './journey-node-icon'
import { JourneyNodeLabel } from './journey-node-label'
import { getClassNames } from '../../../../_utils/classnames'
import { useJourneyContext, useEnrichmentCallbacks } from '../../context'
import { parseLoadedVariant } from '../../../../components/notification-builder/helpers'
import { AppState } from '../../../../stores/app'
import { parseDomainNotificationBuildFromVariant } from '../../action-helpers'
import { getItemStrategy, isActionStep, isSendActionOfType, JourneyNode } from '../../types/journey-nodes'
import { updateStepConfig } from '@pushly/models/lib/structs/journeys'
import type { ISendNotificationActionParams } from '@pushly/models/lib/structs/journeys'
// Type-only import: the shared feed-step params view model. `import type` keeps
// this a compile-time-only dependency so the node does not pull the editor
// module (and its AQE feed-form helpers) into the runtime graph.
import type { FeedNotificationParams } from '../../editors/send-feed-notification-editor/helpers'
import { InvalidJourneyComponent } from '../../edge-types/custom-edge/invalid-journey-component'
import { JourneyNodeInteractionActions } from './journey-node-interaction-actions'
import './custom-node.scss'

import { StepType, JourneyActionType } from '@pushly/models/lib/enums/journeys'
import { JourneyAction } from '../../types/journey-context'
import { PlaceholderReplacementPicker } from './placeholder-replacement-picker'
import { CONVERGENCE_PLACEHOLDER_SIZE } from '../../constants'

export const CustomNode = ({ id, data, targetPosition, sourcePosition }: NodeProps<JourneyNode>) => {
    const [state, dispatch] = useJourneyContext()
    const enrichment = useEnrichmentCallbacks()
    const { getNode } = useReactFlow<JourneyNode>()
    const appState = useService(AppState)
    const notifSvc = useService(NotificationService)
    const feedSvc = useService(FeedService)
    const [loadingDetails, setLoadingDetails] = useState(false)

    const step = data.step

    const handleNodeBodyClick = useCallback(() => {
        // Placeholder nodes use a popover picker — don't close it on the same click
        if (step.type === StepType.PLACEHOLDER) return
        // Close popovers when clicking node body (not buttons)
        dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
    }, [dispatch, step.type])

    // onComponentMount effect — hydrate async step display data ("enrichment").
    //
    // Action steps reference a notification by id (not the full payload) and a
    // Send Feed Notification step references a feed by `feed_id` (not its display
    // name). Both are fetched here in parallel and merged into a SINGLE
    // ON_NODE_ENRICH dispatch: each hydration derives from the same mount-time
    // `step`, so dispatching them separately would clobber one another. The
    // ref-counted semaphore (onEnrichStart/onEnrichEnd) brackets the whole fetch
    // so snapshotReady / dirty detection still waits for enrichment to finish.
    useEffect(() => {
        const enrich = async () => {
            if (!isActionStep(step)) return

            const actionConfig = step.configuration
            const notificationId = actionConfig.params.notification_id
            // Read through `step` (not the pre-guard `actionConfig` alias, which is the
            // same object) so isSendActionOfType's narrowing supplies the feed params.
            const feedId = isSendActionOfType(step, JourneyActionType.SEND_FEED_NOTIFICATION)
                ? step.configuration.params.feed_id
                : undefined

            // Nothing to hydrate — skip the semaphore entirely to avoid a spurious
            // pending count that would delay the initial snapshot.
            if (!notificationId && feedId == null) return

            enrichment?.onEnrichStart()
            setLoadingDetails(true)

            try {
                const [notif, feedRes] = await Promise.all([
                    notificationId
                        ? notifSvc.fetchNotificationById(state.domain.id, notificationId, {
                              query: { include_schedules: false },
                              version: ApiVersion.V4,
                          })
                        : Promise.resolve(undefined),
                    feedId != null ? feedSvc.fetchFeedById(state.domain.id, feedId) : Promise.resolve(undefined),
                ])

                // Compose both hydrations onto the existing params, preserving
                // everything already stored (e.g. feed_id, collapse_key).
                // Assertion retained deliberately: this composition runs for EVERY action
                // step (notification hydration applies to all of them), so `step` is not
                // narrowed to a feed step here. An annotation would not compile either —
                // `IActionStepConfiguration['params']` includes the recommendation shape,
                // whose `notification` is the wire type rather than the builder's
                // CreateDomainNotificationRequest.
                let nextParams = { ...actionConfig.params } as FeedNotificationParams
                let didEnrich = false

                if (notif) {
                    const variant = parseLoadedVariant(notif)
                    const createReq = parseDomainNotificationBuildFromVariant(variant, appState.flags, state.domain)
                    const itemStrategy = getItemStrategy(step)
                    nextParams = {
                        ...nextParams,
                        notification: createReq,
                        notification_id: notificationId,
                        ...(itemStrategy ? { item_strategy: itemStrategy } : {}),
                    }
                    didEnrich = true
                }

                if (feedId != null && feedRes?.ok) {
                    const feed = Feed.from(feedRes.data)
                    nextParams = {
                        ...nextParams,
                        feed: { id: feed.id, name: feed.name, type: feed.feedType },
                    }
                    didEnrich = true
                } else if (feedId != null && !feedRes?.cancelled) {
                    // A failed feed lookup is a hydration failure, NOT a step
                    // configuration error: it must not mark the step invalid via
                    // `uiMeta` (that is reserved for a MISSING `feed_id` — see
                    // `applyFeedNotificationValidation`), otherwise a transient network
                    // blip could block a save. It also must not raise a per-node toast:
                    // a canvas can hold many feed steps, and `FeedService.fetchFeedById`
                    // already surfaces the user-facing error for HTTP failures. Log
                    // instead so the specific node/feed that failed to hydrate is
                    // attributable; the node just renders its un-hydrated subtitle.
                    // A cancelled request is an intentional supersede and stays silent.
                    console.warn('CustomNode: feed enrichment failed', {
                        nodeId: id,
                        domainId: state.domain.id,
                        feedId,
                    })
                }

                // Nothing resolved (e.g. notification not found) — skip the dispatch
                // to avoid a redundant node replacement.
                if (!didEnrich) return

                const updatedStep = updateStepConfig(step, { params: nextParams as ISendNotificationActionParams })

                dispatch({ action: JourneyAction.ON_NODE_ENRICH, data: { nodeId: id, enrichedStep: updatedStep } })
            } finally {
                setLoadingDetails(false)
                enrichment?.onEnrichEnd()
            }
        }

        enrich()
    }, [])

    const isPlaceholder = step.type === StepType.PLACEHOLDER
    const placeholderRole = data.uiMeta?.placeholderRole
    const isConvergence = isPlaceholder && placeholderRole === 'convergence'

    // Convergence placeholders render as a minimal dot/indicator — no card, no replacement picker.
    // The wrapper is sized to match the dot so React Flow's edge connection points align correctly.
    if (isConvergence) {
        return (
            <div
                style={{
                    width: CONVERGENCE_PLACEHOLDER_SIZE,
                    height: CONVERGENCE_PLACEHOLDER_SIZE,
                    position: 'relative',
                }}
            >
                <Handle
                    type="target"
                    position={targetPosition ?? Position.Top}
                    id={getEdgeId(id)}
                    style={{ opacity: 0, width: 0, height: 0, top: 0 }}
                />
                <div className="convergence-dot" />
                <Handle
                    type="source"
                    position={sourcePosition ?? Position.Bottom}
                    id={getEdgeId(id)}
                    style={{ opacity: 0, width: 0, height: 0, bottom: 0 }}
                />
            </div>
        )
    }

    const nodeBody = (
        <div
            className={getClassNames(
                null,
                `custom-node-wrapper type-${step.type} ${
                    step.configuration.type ? `subType-${String(step.configuration.type).toLowerCase()}` : ''
                }`,
                {
                    invalid: data.uiMeta.invalid,
                    'placeholder-role-empty': isPlaceholder && placeholderRole === 'empty',
                    'placeholder-role-convergence': isPlaceholder && placeholderRole === 'convergence',
                },
            )}
            onClick={handleNodeBodyClick}
        >
            <div className="custom-node-border" />
            <div className={getClassNames(null, 'custom-node', 'no-drag')}>
                {isPlaceholder ? (
                    placeholderRole === 'empty' ? (
                        <span className="placeholder-label">Add your first step</span>
                    ) : (
                        <>&nbsp;</>
                    )
                ) : (
                    <>
                        <JourneyNodeIcon id={id} node={step} />
                        <JourneyNodeLabel node={step} loading={loadingDetails} />
                        {![StepType.TRIGGER, StepType.EXIT].includes(step.type) && (
                            <JourneyNodeInteractionActions nodeId={id} enriching={loadingDetails} />
                        )}
                    </>
                )}
            </div>
        </div>
    )

    return (
        <React.Fragment>
            {![StepType.TRIGGER].includes(step.type) && (
                <Handle type="target" position={targetPosition ?? Position.Top} id={getEdgeId(id)} />
            )}
            <NodeToolbar isVisible={!!data.uiMeta.invalid} position={Position.Left} className="journey-invalid-label">
                <InvalidJourneyComponent content={data.uiMeta.invalid?.reason} />
            </NodeToolbar>
            {isPlaceholder ? (
                <PlaceholderReplacementPicker node={getNode(id)!}>{nodeBody}</PlaceholderReplacementPicker>
            ) : (
                nodeBody
            )}
            {step.type !== StepType.EXIT && (
                <Handle type="source" position={sourcePosition ?? Position.Bottom} id={getEdgeId(id)} />
            )}
        </React.Fragment>
    )
}
